import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import staffExtendedService, { StaffDocument } from '../../services/staffExtendedService';

interface DocumentsProps {
  staffMembers: any[];
}

const Documents: React.FC<DocumentsProps> = ({ staffMembers }) => {
  const [documents, setDocuments] = useState<StaffDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedType, setSelectedType] = useState<string>('all');

  useEffect(() => {
    loadDocuments();
  }, [selectedType]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const filters = selectedType !== 'all' ? { document_type: selectedType } : undefined;
      const { data } = await staffExtendedService.getDocuments(filters);
      setDocuments(data);
    } catch (err) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await staffExtendedService.deleteDocument(id);
      setDocuments(documents.filter(d => d.id !== id));
      toast.success('Document deleted');
    } catch (err) {
      toast.error('Failed to delete document');
    }
  };

  const documentTypes = [
    { value: 'all', label: 'All Documents' },
    { value: 'contract', label: 'Contracts' },
    { value: 'certification', label: 'Certifications' },
    { value: 'policy', label: 'Policies' },
    { value: 'review', label: 'Reviews' },
    { value: 'identification', label: 'ID Documents' },
    { value: 'tax_form', label: 'Tax Forms' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-2xl font-bold text-gray-800">Staff Documents</h3>
        <button 
          onClick={() => setShowUploadModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
        >
          Upload Document
        </button>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {documentTypes.map(type => (
          <button
            key={type.value}
            onClick={() => setSelectedType(type.value)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap transition ${
              selectedType === type.value
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Documents Grid */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        {loading ? (
          <p className="text-center text-gray-500">Loading...</p>
        ) : documents.length === 0 ? (
          <p className="text-center text-gray-500">No documents found</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {documents.map(doc => (
              <div key={doc.id} className="border rounded-lg p-4 hover:shadow-md transition">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <div>
                      <p className="font-semibold text-sm">{doc.title}</p>
                      <p className="text-xs text-gray-500 capitalize">{doc.document_type}</p>
                    </div>
                  </div>
                  {doc.is_confidential && (
                    <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">🔒</span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mb-2">{doc.file_name}</p>
                {doc.expiry_date && (
                  <p className="text-xs text-gray-500 mb-2">
                    Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                  </p>
                )}
                <div className="flex gap-2 mt-3">
                  <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    View
                  </a>
                  <button
                    onClick={() => doc.id && handleDelete(doc.id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal Placeholder */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUploadModal(false)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-4">Upload Document</h3>
            <p className="text-gray-600 mb-4">Document upload functionality - integrate with file storage service (e.g., Supabase Storage)</p>
            <button
              onClick={() => setShowUploadModal(false)}
              className="w-full bg-gray-200 px-4 py-2 rounded-lg hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
